import * as React from 'react'
import clsx from 'clsx'
import { useControlled } from '../../hooks/useControlled'
import { useEventCallback } from '../../hooks/useEventCallback'
import { TextField, BaseTextFieldProps } from '../TextField'
import { Chip, ChipProps } from '../Chip'
import styles from './index.module.scss'
import { Box } from '../Box'
export interface InputWithTagsClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element if focused. */
  focused: string
  /** Styles applied to the tag elements, e.g. the chips. */
  tag: string
  /** Styles applied to the tag elements, e.g. the chips if `size="small"`. */
  tagSizeSmall: string
  /** Styles applied to the tag elements, e.g. the chips if `size="medium"`. */
  tagSizeMedium: string
}

export type RenderGetTagProps = ({ index }: { index: number }) => {
  key: number
  className?: string
  disabled?: boolean
  'data-tag-index': number
  tabIndex: number
  onDelete: (event: any) => void
}

export type ChangeReason =
  | 'createOption'
  | 'removeOption'
  | 'createMultipleOption'

export interface InputWithTagsProps<T>
  extends Omit<BaseTextFieldProps<T>, 'onChange' | 'default'> {
  classes?: Partial<InputWithTagsClasses>
  /**
   * The input value.
   */
  inputValue?: string
  /**
   * The value of the autocomplete.
   */
  value?: T[]
  /**
   * The default value. Use when the component is not controlled.
   * @default props.multiple ? [] : null
   */
  defaultValue?: T[]
  /**
   * Callback fired when the value changes.
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   * @param {T|T[]} value The new value of the component.
   */
  onChange?: (
    event: React.SyntheticEvent,
    value: T[],
    option: T,
    reason: ChangeReason
  ) => void
  /**
   * Callback fired when the input value changes.
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   * @param {string} value The new value of the text input.
   */
  onInputChange?: (event: React.SyntheticEvent, value: string) => void
  /**
   * The label to display when the tags are truncated (`limitTags`).
   *
   * @param {number} more The number of truncated tags.
   * @returns {ReactNode}
   * @default (more) => `+${more}`
   */
  getLimitTagsText?: (more: number) => React.ReactNode
  /**
   * The maximum number of tags that will be visible when not focused.
   * Set `-1` to disable the limit.
   * @default -1
   */
  limitTags?: number
  /**
   * Render the selected value.
   *
   * @param {T[]} value The `value` provided to the component.
   * @param {function} getTagProps A tag props getter.
   * @param {object} ownerState The state of the Autocomplete component.
   * @returns {ReactNode}
   */
  renderTags?: (
    value: T[],
    getTagProps: RenderGetTagProps
  ) => React.ReactNode | React.ReactNode[]
  /**
   * Props applied to the [`Chip`](/material-ui/api/chip/) element.
   */
  ChipProps?: ChipProps
  /**
   * Used to determine the string value for a given option.
   **/
  getOptionLabel?: (option: T) => string
  /**
   * createOption creates new option
   */
  createOption?: (option: string) => T
  /**
   * removeDisabledTags will allow removing disabled tags when pressed backspace or delete
   * @default true
   */
  removeDisabledTags?: boolean
  /**
   * createOptionOnBlur will create option on blur
   * @default false
   */
  createOptionOnBlur?: boolean
  /**
   * create option when the split character is detected i.e comman, etc
   * @default false
   */
  splitCharacter?: string | false
}

type Direction = 'next' | 'previous'

const componentName = 'InputWithTags'

export const InputWithTags = <T,>(props: InputWithTagsProps<T>) => {
  const {
    inputValue: inputValueProp,
    value: valueProp,
    defaultValue = [],
    onChange,
    onInputChange,
    classes,
    disabled,
    limitTags = -1,
    renderTags,
    getOptionLabel: getOptionLabelProp = option => option ?? option,
    getLimitTagsText = more => `+${more}`,
    ChipProps,
    createOption: createOptionProps = option => option,
    size = 'medium',
    removeDisabledTags = true,
    createOptionOnBlur = false,
    splitCharacter = false,
    ...others
  } = props
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)
  const [focusedTag, setFocusedTag] = React.useState(-1)
  const [focused, setFocused] = React.useState(false)

  const [value, setValueState] = useControlled({
    controlled: valueProp,
    default: defaultValue,
    name: componentName,
  })

  const [inputValue, setInputValueState] = useControlled({
    controlled: inputValueProp,
    default: '',
    name: componentName,
    state: 'inputValue',
  })

  const focusTag = useEventCallback(tagToFocus => {
    if (tagToFocus === -1) {
      inputRef.current?.focus()
    } else {
      // @ts-ignore
      anchorEl?.querySelector(`[data-tag-index="${tagToFocus}"]`)?.focus()
    }
  })

  function validTagIndex(index: number, direction: Direction) {
    if (index === -1) {
      return -1
    }

    let nextFocus = index

    /* eslint-disable */
    while (true) {
      // Out of range
      if (
        (direction === 'next' && nextFocus === value.length) ||
        (direction === 'previous' && nextFocus === -1)
      ) {
        return -1
      }

      const option = anchorEl?.querySelector(`[data-tag-index="${nextFocus}"]`)

      if (
        !option ||
        !option.hasAttribute('tabindex') ||
        // @ts-ignore
        option?.disabled ||
        option.getAttribute('aria-disabled') === 'true'
      ) {
        nextFocus += direction === 'next' ? 1 : -1
      } else {
        return nextFocus
      }
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (inputValue !== newValue) {
      setInputValueState(newValue)

      if (onInputChange) {
        onInputChange(event, newValue)
      }
    }
  }

  const handleFocusTag = (event: KeyboardEvent, direction: Direction) => {
    let nextTag = focusedTag
    if (focusedTag === -1) {
      if (inputValue === '' && direction === 'previous') {
        nextTag = value.length - 1
      }
    } else {
      nextTag += direction === 'next' ? 1 : -1
      if (nextTag < 0) {
        nextTag = 0
      }
      if (nextTag === value.length) {
        nextTag = -1
      }
    }
    nextTag = validTagIndex(nextTag, direction)
    setFocusedTag(nextTag)
    focusTag(nextTag)
  }

  // Ensure the focusedTag is never inconsistent
  React.useEffect(() => {
    if (focusedTag > value.length - 1) {
      setFocusedTag(-1)
      focusTag(-1)
    }
  }, [value, focusedTag, focusTag])

  const handleValue = (
    event: KeyboardEvent,
    newValue: T[],
    option: T,
    reason: ChangeReason
  ) => {
    if (
      value.length === newValue.length &&
      value.every((val, i) => val === newValue[i])
    ) {
      return
    }

    if (onChange) {
      onChange(event as any, newValue, option, reason)
    }

    setValueState(newValue)
  }

  const selectNewValue = (event: KeyboardEvent, option: string) => {
    let newValue: T[] | string = option

    newValue = Array.isArray(value) ? value.slice() : []

    const newOption = createOptionProps(option) as T
    newValue.push(newOption)

    setInputValueState('')
    if (onInputChange) {
      onInputChange(event as any, '')
    }

    handleValue(event, newValue, newOption, 'createOption')
  }

  const handleFocus = () => {
    setFocused(true)
  }
  const handleBlur = (e: any) => {
    setFocused(false)
    // @ts-ignore
    if (createOptionOnBlur && inputValue) {
      selectNewValue(e, inputValue)
    }
  }

  const handleTagDelete = (index: number) => (event: React.SyntheticEvent) => {
    const newValue = value.slice()
    const removedValue = newValue.splice(index, 1)
    handleValue(event as any, newValue, removedValue[0]!, 'removeOption')
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      focusedTag !== -1 &&
      ['ArrowLeft', 'ArrowRight'].indexOf(event.key) === -1
    ) {
      setFocusedTag(-1)
      focusTag(-1)
    }
    if (event.which !== 229) {
      switch (event.key) {
        case 'ArrowLeft':
          handleFocusTag(event, 'previous')
          break
        case 'ArrowRight':
          handleFocusTag(event, 'next')
          break
        case 'Enter':
          if (inputValue !== '') {
            event.preventDefault()
            selectNewValue(event, inputValue)
          }
          break
        case 'Backspace':
          if (inputValue === '' && value.length > 0) {
            let index = focusedTag === -1 ? value.length - 1 : focusedTag
            if (!removeDisabledTags) {
              index = validTagIndex(index, 'previous')
            }
            const newValue = value.slice()
            const removedValue = newValue.splice(index, 1)
            handleValue(event, newValue, removedValue[0]!, 'removeOption')
          }
          break
        case 'Delete':
          if (inputValue === '' && value.length > 0 && focusedTag !== -1) {
            let index = focusedTag
            if (!removeDisabledTags) {
              index = validTagIndex(index, 'previous')
            }
            const newValue = value.slice()
            const removedValue = newValue.splice(index, 1)
            handleValue(event, newValue, removedValue[0]!, 'removeOption')
          }
        case splitCharacter:
          if (inputValue !== '') {
            event.preventDefault()
            selectNewValue(event, inputValue)
          }
          break
        default:
      }
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const data = event.clipboardData.getData('Text')
    if (!Boolean(data)) {
      return
    }
    if (splitCharacter) {
      let result = data.split(splitCharacter)
      let newValue = Array.isArray(value) ? value.slice() : []
      const newOption = result.map(option => createOptionProps(option)) as T[]
      newValue = [...newValue, ...newOption]
      if (onInputChange) {
        onInputChange(event as any, '')
      }
      if (onChange) {
        onChange(
          event as any,
          newValue,
          newOption as any,
          'createMultipleOption'
        )
      }
      setValueState(newValue)
    } else {
      if (onInputChange) {
        onInputChange(event as any, data)
      }
    }
    props?.inputProps?.onPaste?.(event)
  }

  let centerAdornment: React.ReactNode[] = []
  const chipSize = {
    small: 'exSmall',
    medium: 'small',
    large: 'medium',
  } as const

  if (value.length > 0) {
    const getCustomizedTagProps = (params: any) => ({
      className: classes?.tag,
      disabled,
      'data-tag-index': params?.index,
      key: params?.index,
      tabIndex: -1,
      ...(!props.InputProps?.readOnly && {
        onDelete: handleTagDelete(params?.index),
      }),
    })
    if (renderTags) {
      centerAdornment = renderTags(
        value,
        getCustomizedTagProps as RenderGetTagProps
      ) as React.ReactNode[]
    } else {
      centerAdornment = value.map((option, index) => (
        <Chip
          label={getOptionLabelProp(option) as string}
          size={chipSize[size]}
          {...getCustomizedTagProps({ index })}
          {...ChipProps}
        />
      ))
    }
  }
  const _focused = focused || focusedTag !== -1

  if (limitTags > -1 && Array.isArray(centerAdornment)) {
    const more = centerAdornment.length - limitTags
    if (!_focused && more > 0) {
      centerAdornment = centerAdornment.splice(0, limitTags)
      centerAdornment.push(
        <span className={classes?.tag} key={centerAdornment.length}>
          {getLimitTagsText(more)}
        </span>
      )
    }
  }
  const textFieldRef = React.useRef<HTMLDivElement>(null)
  const startAdornmentRef = React.useRef<HTMLDivElement>(null)
  const endAdornmentRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    let adornmentWidth = 0
    if (props.InputProps?.startAdornment && props.InputProps?.endAdornment) {
      adornmentWidth =
        startAdornmentRef.current?.clientWidth! +
        endAdornmentRef.current?.clientWidth!
    } else if (props.InputProps?.startAdornment) {
      adornmentWidth = startAdornmentRef.current?.clientWidth!
    } else if (props.InputProps?.endAdornment) {
      adornmentWidth = endAdornmentRef.current?.clientWidth!
    }
    textFieldRef.current?.style.setProperty(
      '--adornmentWidth',
      `${adornmentWidth}px`
    )
  }, [])

  return (
    // @ts-ignore
    <div onKeyDown={handleKeyDown} tabIndex={1}>
      <TextField
        ref={textFieldRef}
        {...others}
        InputProps={{
          ...props.InputProps,
          // @ts-ignore
          ref: setAnchorEl,
          startAdornment: props.InputProps?.startAdornment && (
            <Box ref={startAdornmentRef} className={styles.adornedStartWrapper}>
              {props.InputProps?.startAdornment}
            </Box>
          ),
          centerAdornment: centerAdornment,
          endAdornment: props.InputProps?.endAdornment && (
            <Box ref={endAdornmentRef} className={styles.adornedEndWrapper}>
              {props.InputProps?.endAdornment}
            </Box>
          ),
          classes: {
            ...props.InputProps?.classes,
            root: clsx(
              styles.inputContainer,
              styles[`size-${size}`],
              props.InputProps?.classes?.root
            ),
            adornedCenterWrapper: styles.adornedCenterWrapper,
          },
        }}
        inputProps={{
          ...props.inputProps,
          value: inputValue,
          onChange: handleInputChange,
          onPaste: handlePaste,
          ref: inputRef,
          autoCapitalize: 'none',
          spellCheck: 'false',
          classes: {
            ...props.inputProps?.classes,
            input: clsx(styles.input, props.inputProps?.classes?.input),
          },
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        size={size}
        classes={{ root: styles.root }}
      />
    </div>
  )
}
